/* eslint-disable @typescript-eslint/ban-ts-comment */
import * as R from 'ramda'
import * as Sentry from '@sentry/react'
import { ResponseAction } from '@weblite/ts-action/types'
import {
  reqSignUpUser,
  ResSignUpUserPayload,
  SignUpUser,
} from '@weblite/ts-action/client-user'
import { ProfileUpdateDto } from '@weblite/sahab-profile-action/profile'
import {
  ActiveSession,
  GetActiveSessions,
  reqGetActiveSessions,
  reqLogout,
  reqResendCode,
  reqTerminateSessions,
  TerminateSessions,
} from '@weblite/ts-action/auth'
// @ts-ignore
import { dataAuthSignin, dataAuthVerify } from '@weblite/action/auth'

import { httpRequest } from '$setup/http'
import { showErrorSnackbar } from '$comp/snackbars'
import {
  closeAuthVerify,
  goToAuthSignup,
  initializeMainApp,
} from '$logic/router'
import { clientUserStorage } from '$logic/client-user/storage'
import { t } from '$i18n'
import { normalizePhone } from '$func/phone'
import { migrateFromTempSessionIdToSessionId } from '$logic/analytics'
import { afterSignUp } from '$hooks/after/signup'
import { request } from '$setup/ws'
import { sendEventToTwa } from '$logic/twaCommunication/twaCommunication.api'
import { updateTwaPermissions } from '$logic/twaCommunication/service'
import { TWA_EVENTS } from '$logic/twaCommunication'
import { authStorage } from './storage'
import beforeSignIn from '../../hooks/before/signin'
import afterSignIn from '../../hooks/after/signin'
import afterSignOut from '../../hooks/after/signout'

export type VerificationSession = { phone: string; sessionId: string }

export async function signInUser(
  inputPhone: string,
): Promise<VerificationSession> {
  const phone = normalizePhone(inputPhone)
  const response = await httpRequest(dataAuthSignin({ phone }))
  if (!response) return null

  if (response.payload?.error) {
    showErrorSnackbar(t(`signin.serverCodes.${response.payload.error.code}`))
    return null
  }
  return response.payload
}

export async function resendCode(phone: string, sessionId: string) {
  const response = await httpRequest(reqResendCode({ phone, sessionId }))
  if (!response) return null
  if (response.error) {
    closeAuthVerify()
    return null
  }

  return true
}

function loginEAEvent(userId: string) {
  window.gtag?.('event', 'login', { user_id: userId })
}

function signUpEAEvent(userId: string) {
  window.gtag?.('event', 'sign_up', { user_id: userId })
}

export type VerifyError = 'WRONG_CODE' | 'SERVER_ERROR'
// eslint-disable-next-line consistent-return
export async function verifyPhone(
  phone: string,
  sessionId: string,
  code: string,
): Promise<void | VerifyError> {
  const response = await httpRequest(dataAuthVerify({ phone, sessionId, code }))

  if (!response) return 'SERVER_ERROR'

  if (response.payload.error) {
    if (response.payload.error.code === 'MAX_TRIES_REACHED') {
      showErrorSnackbar(t('signin.verification.maxTriesReached'))
      closeAuthVerify()
      // eslint-disable-next-line consistent-return
      return
    }
    if (response.payload.error.code === 'SIGNUP_CLOSED') {
      showErrorSnackbar(t('signin.verification.signupClosed'))
      closeAuthVerify()
      // eslint-disable-next-line consistent-return
      return
    }

    return response.payload.error.code
  }

  const { token, invited, ...clientUser } = response.payload
  const isUserSignedUp = 'id' in clientUser

  authStorage.set({ token, isSignedUp: isUserSignedUp } as Auth)

  if (isUserSignedUp) {
    Sentry.setUser(R.pick(['id', 'username'], clientUser))
    loginEAEvent(clientUser.id)
    await beforeSignIn()
    await clientUserStorage.set(clientUser)
    await initializeMainApp()
    await afterSignIn()
    updateTwaPermissions()
    migrateFromTempSessionIdToSessionId()
  } else {
    await goToAuthSignup(invited)
  }
}

export async function signUpUser(
  firstname: string,
  lastname: string,
  profileData: ProfileUpdateDto = {},
  refCode?: string,
): Promise<void | string> {
  const response: ResponseAction<ResSignUpUserPayload> = await httpRequest(
    reqSignUpUser({ firstname, lastname, profileData, refCode }),
  )
  if (!response) return
  const { payload, error } = response

  const errorCode = error?.code as SignUpUser['ErrorCodes']
  if (errorCode) return errorCode

  const { token, user } = payload
  Sentry.setUser(R.pick(['id', 'username'], user))
  authStorage.set({ token, isSignedUp: true } as Auth)
  signUpEAEvent(user.id)
  await afterSignUp()
  await beforeSignIn()
  await clientUserStorage.set(user)
  await initializeMainApp()
  await afterSignIn()
  updateTwaPermissions()
  await migrateFromTempSessionIdToSessionId()
  analite('USER_SIGNUP')
}

export async function terminateSessions(selectors: string[]) {
  await request<TerminateSessions>(reqTerminateSessions({ selectors }))
}

export async function logOutUser() {
  if (await httpRequest(reqLogout({}))) {
    await sendEventToTwa(TWA_EVENTS.LOGOUT)
    analite('PROFILE_SETTING_LOGOUT')
    Sentry.setUser(null)
    afterSignOut()
  }
}

export function loginUserFromAndroid(token: string) {
  authStorage.set({ token, isSignedUp: true })
}

export function getAuthToken() {
  return authStorage.get()?.token
}

export function isLoggedIn() {
  // return !!(getAuthToken() && authStorage.get()?.userId)
  return (
    !!getAuthToken() &&
    (authStorage.get()?.isSignedUp || authStorage.get()?.userId)
  )
}

export async function getActiveSessions() {
  const res = await request<GetActiveSessions>(reqGetActiveSessions({}))
  if (res.error) return null
  const { sessions, selector } = res.payload
  return {
    current: R.find(R.propEq('selector', selector), sessions) as ActiveSession,
    others: R.reject(
      R.propEq('selector', selector),
      sessions,
    ) as ActiveSession[],
  }
}
